import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, money, formatApiErrorDetail } from "@/api";
import { useAuth } from "@/AuthContext";
import { toast } from "sonner";
import { CalendarDays, MapPin, Users, Clock, Check, Loader2, ShieldCheck } from "lucide-react";
import { dateRange } from "@/pages/Camps";

export default function CampDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [camp, setCamp] = useState(null);
  const [athlete, setAthlete] = useState("");
  const [notes, setNotes] = useState("");
  const [fees, setFees] = useState({ client_fee_percent: 2.5 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/camps/${id}`).then((r) => setCamp(r.data)).catch(() => nav("/camps"));
    api.get("/pricing/fees").then((r) => setFees(r.data)).catch(() => {});
  }, [id, nav]);

  const book = async () => {
    if (!user) { toast.error("Log in to book a camp"); nav("/login"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/camps/${id}/checkout`, {
        athlete_name: athlete, notes, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  };

  if (!camp) return <div className="p-20 text-center text-zinc-500">Loading camp…</div>;

  const fee = Math.floor(camp.price * 100 * (fees.client_fee_percent ?? 2.5) / 100 + 0.5) / 100;

  return (
    <div data-testid="camp-detail-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
      <Link to="/camps" className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-[#00e5ff] transition-colors duration-200">← All camps</Link>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-6">
        <div className="lg:col-span-7 space-y-10">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#00e5ff]">
              <CalendarDays className="w-3.5 h-3.5" /> {dateRange(camp.start_date, camp.end_date)}
              {camp.daily_time && <span className="text-zinc-500">· daily at {camp.daily_time}</span>}
            </div>
            <h1 data-testid="camp-title" className="display text-4xl sm:text-5xl uppercase tracking-tighter font-bold mt-4 leading-[0.95]">{camp.title}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-zinc-400">
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-[#00e5ff]" /> {camp.city}{camp.region ? `, ${camp.region}` : ""}{camp.venue ? ` · ${camp.venue}` : ""}</span>
              <span className="flex items-center gap-1"><Users className="w-3 h-3 text-[#00e5ff]" /> {camp.spots_left} of {camp.capacity} spots left</span>
              <span className="flex items-center gap-1">
                {camp.coach_name} {camp.verified && <ShieldCheck className="w-3 h-3 text-[#00e5ff]" />}
              </span>
            </div>
          </div>

          {camp.description && (
            <div>
              <h2 className="display text-2xl uppercase tracking-tight font-bold">About this camp</h2>
              <p className="text-sm text-zinc-300 leading-relaxed mt-4 whitespace-pre-line">{camp.description}</p>
            </div>
          )}

          {camp.includes?.length > 0 && (
            <div>
              <h3 className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">What's included</h3>
              <ul className="mt-4 space-y-2">
                {camp.includes.map((i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-zinc-300"><Check className="w-4 h-4 text-[#00e5ff]" /> {i}</li>
                ))}
              </ul>
            </div>
          )}

          {camp.levels?.length > 0 && (
            <div>
              <h3 className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">Levels</h3>
              <div className="flex flex-wrap gap-2 mt-3">
                {camp.levels.map((l) => <span key={l} className="text-[11px] uppercase border border-white/15 text-zinc-300 px-3 py-1.5">{l}</span>)}
              </div>
            </div>
          )}

          <Link to={`/coaches/${camp.coach_id}`} data-testid="camp-coach-link" className="inline-flex items-center gap-3 border border-white/10 p-4 hover:border-[#00e5ff]/50 transition-colors duration-200">
            {camp.coach_photo && <img src={camp.coach_photo} alt={camp.coach_name} className="w-12 h-12 object-cover border border-white/10" />}
            <span>
              <span className="display uppercase font-bold block leading-none">{camp.coach_name}</span>
              <span className="text-[11px] text-zinc-500">{camp.category} · view full profile</span>
            </span>
          </Link>
        </div>

        <div className="lg:col-span-5 lg:sticky lg:top-24 self-start">
          <div className="bg-[#121212] border border-white/10 p-6 lg:p-8 space-y-5">
            <div>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Book the whole camp</span>
              <p className="display text-4xl font-bold text-[#00e5ff] leading-none mt-2">{money(camp.price)}</p>
              <p className="text-[11px] text-zinc-500 mt-2 flex items-center gap-1"><Clock className="w-3 h-3" /> One payment covers every day</p>
            </div>

            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Camper's name</span>
              <input data-testid="camp-athlete-input" value={athlete} onChange={(e) => setAthlete(e.target.value)}
                placeholder="Who is attending?"
                className="mt-2 w-full bg-black border border-white/10 text-sm px-3 py-2.5 focus:border-[#00e5ff] focus:outline-none focus:ring-1 focus:ring-[#00e5ff] transition-colors duration-200" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Notes for the coach</span>
              <textarea data-testid="camp-notes-input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Age, level, anything the coach should know…"
                className="mt-2 w-full bg-black border border-white/10 text-sm px-3 py-2.5 focus:border-[#00e5ff] focus:outline-none focus:ring-1 focus:ring-[#00e5ff] transition-colors duration-200" />
            </label>

            <div className="border-t border-white/10 pt-4 space-y-2">
              <div className="flex items-center justify-between text-xs text-zinc-400">
                <span>Camp price</span><span>{money(camp.price)}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-zinc-400">
                <span>Service fee ({fees.client_fee_percent ?? 2.5}%)</span>
                <span data-testid="camp-service-fee">${fee.toFixed(2)}</span>
              </div>
              <div className="flex items-end justify-between pt-3">
                <div>
                  <span className="text-[10px] uppercase text-zinc-500 block">Total today</span>
                  <span data-testid="camp-total" className="display text-3xl font-bold text-[#00e5ff] leading-none">${(camp.price + fee).toFixed(2)}</span>
                </div>
                <button data-testid="camp-book-btn" disabled={busy || camp.spots_left === 0} onClick={book}
                  className="bg-[#00e5ff] text-black font-bold uppercase tracking-wide px-7 py-4 flex items-center gap-2 hover:bg-[#00b3cc] disabled:opacity-40 transition-colors duration-200">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} {camp.spots_left === 0 ? "Camp full" : "Book camp"}
                </button>
              </div>
            </div>
            <p className="text-[10px] text-zinc-600">Secure card payment via Stripe. Full refund up to {fees.full_refund_hours ?? 24}h before the camp starts.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
